import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { urlFor, client } from "../../client";
import { PortableText } from "@portabletext/react";
import { AppWrap } from "../../wrapper";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { DotLoader } from "react-spinners";
import "./BlogDetails.scss";
import { Helmet } from "react-helmet-async";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { FaCopy, FaCheck } from "react-icons/fa";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import remarkGfm from "remark-gfm";
import { remark } from "remark";
import remarkHtml from "remark-html";

const BlogDetails = () => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [activeHeading, setActiveHeading] = useState(null);
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(true);
    const blogQuery = `*[_type == "blog" && slug.current == $slug][0]{
      title,
      mainImage,
      publishedAt,
      description,
      body,
      author-> { name, bio, image },
      category[]-> { title, slug, _id },
      tags[]-> { title, slug, _id },
      slug,
    }`;
    const recentQuery = `*[_type == "blog"]|order(publishedAt desc)[0...5]{ title, slug, mainImage, publishedAt }`;
    const categoriesQuery = `*[_type == "category"]{ title, slug, _id }`;
    const categoryCountQuery = `*[_type == 'category']{ _id, "count": count(*[_type == 'blog' && references(^._id)]) }`;

    client.fetch(blogQuery, { slug }).then((data) => {
      setBlog(data);
      setLoading(false);
      // Fetch related posts after blog is loaded
      if (data && data.category && data.category.length > 0) {
        const catIds = data.category.map((cat) => cat._id);
        const relatedQuery = `*[_type == "blog" && references($catIds) && slug.current != $slug][0...5]{ title, slug, mainImage, publishedAt }`;
        client.fetch(relatedQuery, { catIds, slug }).then(setRelatedPosts);
      } else {
        setRelatedPosts([]);
      }
    });
    client.fetch(recentQuery).then(setRecentPosts);
    client.fetch(categoriesQuery).then(setCategories);
    client.fetch(categoryCountQuery).then((counts) => {
      const countMap = {};
      counts.forEach((cat) => {
        countMap[cat._id] = cat.count;
      });
      setCategoryCounts(countMap);
    });
  }, [slug]);

  // Extract headings (h1, h2) from blog.body for Table of Contents
  const extractHeadings = (body) => {
    if (!Array.isArray(body)) return [];
    return body
      .filter(
        (block) =>
          block._type === "block" &&
          (block.style === "h1" || block.style === "h2") &&
          Array.isArray(block.children)
      )
      .map((block) => ({
        text: block.children.map((c) => c.text).join(""),
        style: block.style,
        key: block._key || Math.random().toString(36).substr(2, 9),
      }));
  };
  const tocHeadings = extractHeadings(blog?.body);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <DotLoader color="#3498db" size={70} />
        <p className="mt-4 text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!blog) return <div>Blog not found</div>;

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;

    // Handle YouTube videos
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.split("v=")[1]?.split("&")[0] || url.split("/").pop();
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Handle Vimeo videos
    if (url.includes("vimeo.com")) {
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }

    // Handle Google Drive videos
    if (url.includes("drive.google.com")) {
      const fileId = url.match(/[-\w]{25,}/);
      return fileId
        ? `https://drive.google.com/file/d/${fileId}/preview`
        : null;
    }

    return null;
  };

  // Add id to headings in PortableText rendering
  const components = {
    block: {
      normal: ({ children }) => <p>{children}</p>,
      h1: ({ children, value }) => {
        const id = value._key ? `toc-${value._key}` : undefined;
        return <h1 id={id}>{children}</h1>;
      },
      h2: ({ children, value }) => {
        const id = value._key ? `toc-${value._key}` : undefined;
        return <h2 id={id}>{children}</h2>;
      },
      h3: ({ children }) => <h3>{children}</h3>,
    },
    marks: {
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      underline: ({ children }) => <u>{children}</u>,
    },
    types: {
      image: ({ value }) => (
        <img
          src={urlFor(value)}
          alt="Blog content"
          className="blog-content-image"
        />
      ),
      code: ({ value }) => {
        const [copied, setCopied] = useState(false);

        useEffect(() => {
          if (copied) {
            const timer = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timer);
          }
        }, [copied]);

        if ((value.language || "").toLowerCase() === "markdown") {
          let code = value.code || "";
          code = code.trim();
          if (code.startsWith("```")) {
            code = code
              .replace(/^```[a-zA-Z]*\n?/, "")
              .replace(/```$/, "")
              .trim();
          }
          return (
            <div className="markdown-block-container">
              <div dangerouslySetInnerHTML={renderMarkdown(code)} />
            </div>
          );
        }

        return (
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">{value.language || "text"}</span>
              <CopyToClipboard text={value.code} onCopy={() => setCopied(true)}>
                <button className="copy-button">
                  {copied ? <FaCheck color="green" /> : <FaCopy />}
                  {copied ? " Copied!" : " Copy"}
                </button>
              </CopyToClipboard>
            </div>
            <SyntaxHighlighter
              language={value.language || "text"}
              style={atomDark}
              showLineNumbers
              showInlineLineNumbers
              wrapLines
            >
              {value.code}
            </SyntaxHighlighter>
          </div>
        );
      },
      youtube: ({ value }) => {
        const embedUrl = getVideoEmbedUrl(value.url);
        return embedUrl ? (
          <div className="youtube-container">
            <iframe
              width="100%"
              height="400"
              src={embedUrl}
              title="YouTube Video"
              frameBorder="0"
              allowFullScreen
            ></iframe>
          </div>
        ) : null;
      },
    },
  };

  const renderMarkdown = (markdown) => {
    const result = remark()
      .use(remarkGfm)
      .use(remarkHtml)
      .processSync(markdown);
    return { __html: result.toString() };
  };

  return (
    <>
      <Helmet>
        <title>
          {blog.author?.name ? `${blog.author.name} | ` : ""}
          {blog.title}
        </title>
        <meta name="description" content={blog.description} />
        <meta
          name="keywords"
          content={blog.tags?.map((t) => t.title).join(", ")}
        />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.description} />
        <meta property="og:image" content={blog.imageURL} />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="blogdetails-layout">
        <div className="blogdetails-main">
          <div className="app__blog-details">
            <div className="blog-meta-bar">
              <div className="blog-meta-categories">
                {blog.category && blog.category.length > 0 && (
                  <div className="blog-categories-list">
                    {blog.category.map((cat, i) => (
                      <a
                        href={`/categories/${cat.slug?.current || cat.title}`}
                        className="blog-category-badge"
                        style={{ textDecoration: "none" }}
                        key={cat._id || i}
                      >
                        {cat.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="blog-meta-date">
                <span className="p-text" style={{ color: "#6b7688" }}>
                  {"By: "}
                  {blog.author?.name ? `${blog.author.name}  / ` : ""}
                  {new Date(blog.publishedAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
            <img
              src={urlFor(blog.mainImage)}
              alt={blog.title}
              className="blog-image"
              style={{ marginTop: 50 }}
            />
            <h1 className="head-text">{blog.title}</h1>
            {/* Blog Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div
                className="blog-categories-list"
                style={{ margin: "0 0 10px 0" }}
              >
                {blog.tags.map((tag, i) => (
                  <a
                    href={`/tags/${tag.slug?.current || tag.title}`}
                    className="blog-category-badge tag"
                    style={{ textDecoration: "none" }}
                    key={tag._id || i}
                  >
                    #{tag.title}
                  </a>
                ))}
              </div>
            )}
            <div className="blog-content">
              <PortableText value={blog.body} components={components} />
            </div>
            <hr className="blog-divider" />
            {blog.author && (
              <div className="author-info" style={{ marginTop: 20 }}>
                {blog.author.image && (
                  <img
                    src={urlFor(blog.author.image)}
                    alt={blog.author.name}
                    className="author-image"
                  />
                )}
                <div className="author-details">
                  <h3 className="author-name">{blog.author.name}</h3>
                  {blog.author.bio && (
                    <p className="author-bio">{blog.author.bio}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <aside className="blogdetails-sidebar">
          {/* Table of Contents */}
          {tocHeadings.length > 0 && (
            <div className="sidebar-section">
              <h3>Table of Contents</h3>
              <ul className="sidebar-list toc-list">
                {tocHeadings.map((heading) => (
                  <li
                    key={heading.key}
                    className={`toc-item toc-${heading.style} ${
                      activeHeading === heading.key ? "toc-active" : ""
                    }`}
                    onClick={() => setActiveHeading(heading.key)}
                  >
                    <a
                      href={`#toc-${heading.key}`}
                      className="sidebar-link sidebar-link-toc"
                    >
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="sidebar-section">
            <h3>Recent Posts</h3>
            <ul className="sidebar-list">
              {recentPosts.map((post) => (
                <li key={post.slug?.current || post.title}>
                  <Link
                    to={`/blogs/${post.slug?.current || post.title}`}
                    className="sidebar-link"
                  >
                    {post.mainImage && (
                      <img
                        src={urlFor(post.mainImage)}
                        alt={post.title}
                        className="sidebar-thumb"
                      />
                    )}
                    <div>
                      <div className="sidebar-title">{post.title}</div>
                      <div className="sidebar-date">
                        {new Date(post.publishedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {relatedPosts.length > 0 && (
            <div className="sidebar-section">
              <h3>Related Posts</h3>
              <ul className="sidebar-list">
                {relatedPosts.map((post) => (
                  <li key={post.slug?.current || post.title}>
                    <Link
                      to={`/blogs/${post.slug?.current || post.title}`}
                      className="sidebar-link"
                    >
                      {post.mainImage && (
                        <img
                          src={urlFor(post.mainImage)}
                          alt={post.title}
                          className="sidebar-thumb"
                        />
                      )}
                      <div>
                        <div className="sidebar-title">{post.title}</div>
                        <div className="sidebar-date">
                          {new Date(post.publishedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="sidebar-section">
            <h3>Categories</h3>
            <ul className="sidebar-list">
              {categories.map((cat) => (
                <li key={cat._id} className="sidebar-category-item">
                  <a
                    href={`/categories/${cat.slug?.current || cat.title}`}
                    className="sidebar-link sidebar-link-category"
                  >
                    <span className="category-title">{cat.title}</span>
                    <span className="category-count">
                      {categoryCounts[cat._id] || 0} posts
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
      <ScrollToTopButton />
    </>
  );
};

export default AppWrap(BlogDetails, "blogdetails");
